/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { addScormResponseAction } from 'actions/addScormResponseAction';
import { updateScoreAction } from 'actions/updateScoreAction';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useScoringQuizzes } from 'services/apiHelpers/useScoringQuizzes';
import pubSub from 'services/pubSub';
import { unlockInteraction } from 'services/unlockInteraction';
import { colors } from 'styles/variables';
import { interactionContext as InteractionContext } from './interactionContext';
import { onSubmit } from './onSubmit';

import { HostingContext } from 'hosting/context';

const { lGrey0, mint } = colors;

export const SelectOptionsContext = createContext<any>(null);

export type Result = 'correct' | 'incorrect' | 'attempt' | undefined;

const SelectOptionsRoot = ({ children, ...props }) => {
  const interactionContext = useContext(InteractionContext);
  const hostingCtx = useContext(HostingContext);

  const {
    lockDown,
    numberOfAttempts,
    showAttemptNumber = true,
    useMediaInFeedback,
    showCorrectAnswer,
    dividerColor = mint,
    dropDownBorderColor = '#19D1CF',
    navigationBehaviour = {
      correct: false,
      incorrect: false,
    },
    score,
    randomizeOptions = false,
    useFeedback = true,
  } = interactionContext.props;

  const [question, optionsContainer, submitBtn, feedback] = children;
  const options = optionsContainer.props.children;

  const [selectedOptions, setSelectedOptions] = useState<
    (number | undefined)[]
  >(options.map(o => undefined));

  const [attempt, setAttempt] = useState<number>(0);
  const [result, setResult] = useState<Result>(undefined);
  const [showFeedback, setShowFeedback] = useState<Result>(result);
  const [isLive, setIsLive] = useState<boolean | undefined>(undefined);
  const dispatch = useDispatch();

  const scoringQuizzes = useScoringQuizzes();

  const correctAnswers = options.reduce((acc, curr, index) => {
    const { id, correctAnswer = 0 } = curr.props;

    return [...acc, correctAnswer];
  }, []);

  useEffect(() => {
    if (!window.dev()) {
      return;
    }

    const showFeedbackSubscription = pubSub.subscribe(
      `${interactionContext.id}`,
      ({ result }) => {
        console.log(result);
        setResult(result);
        setShowFeedback(result);
      }
    );

    return () => {
      showFeedbackSubscription();
    };
  }, []);

  useEffect(() => {
    if (result || isLive) {
      return;
    }

    //
    if (selectedOptions.every(o => o !== undefined)) {
      setIsLive(true);
    }
  }, [selectedOptions]);

  return (
    <SelectOptionsContext.Provider
      value={{
        selectedOptions,
        setSelectedOptions: (index: number, value: number) => {
          setSelectedOptions(selectedOptions => {
            const updatedSelectedOptions = [...selectedOptions];
            updatedSelectedOptions[index] = value;

            return updatedSelectedOptions;
          });
        },
        isLive,
        setIsLive,
        onSubmit,
        result: showFeedback,
        onClose: () => {
          setShowFeedback(undefined);
        },
        correctAnswers,
      }}
    >
      <form
        id={`select-options-${props?.id}`}
        onSubmit={e => {
          e.preventDefault();
        }}
      >
        <div
          css={css({
            margin: `0 0 ${window.dev() ? 12 : 17}px 0`,
            padding: `0 0 ${window.dev() ? 10 : 15}px 0`,
            borderBottom: `1px solid ${dividerColor}`,
          })}
        >
          {question}
        </div>

        <div
          css={css`
            //
          `}
        >
          <div
            css={css({
              pointerEvents: isLive === false ? 'none' : 'auto',
            })}
            data-select-options-parent-id={optionsContainer?.props?.id}
          >
            {options.map((option, index) => {
              return React.cloneElement(option, {
                index,
              });
            })}
          </div>

          <div></div>

          <div
            css={css({
              margin: '20px 0 0 0',
              ...(!window.dev() && {
                visibility: isLive ? 'visible' : 'hidden',
                pointerEvents: isLive ? 'auto' : 'none',
              }),
            })}
            onClick={
              window.dev()
                ? undefined
                : () => {
                    onSubmit({
                      selectedOptions,
                      correctAnswers,
                      attempt,
                      numberOfAttempts,
                      setAttempt,
                      setResult,
                      setShowFeedback,
                      showCorrectAnswer,
                      setSelectedOptions,
                      interactionContext,
                      options,
                      scoringQuizzes,
                      dispatch,
                      updateScoreAction,
                      addScormResponseAction,
                      unlockInteraction,
                      setIsLive,
                      hostingCtx: hostingCtx,
                      score,
                    });
                  }
            }
          >
            {submitBtn}
          </div>
        </div>

        {useFeedback &&
          showFeedback &&
          React.cloneElement(feedback, {
            result,
            showAttemptNumber,
            attempt,
            numberOfAttempts,
            navigationBehaviour,
            onClose() {
              setShowFeedback(undefined);
            },
            useMediaInFeedback,
          })}
      </form>
    </SelectOptionsContext.Provider>
  );
};

export default SelectOptionsRoot;
